import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FiArrowLeft, FiUser, FiMail, FiLock } from 'react-icons/fi';
import { useTranslation } from 'react-i18next';
import { useNotification } from '../context/NotificationContext';
import { AuthService } from '../services/auth.service';

const MFA_PENDING_KEY = 'mfa_pending';

const RegisterPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { addNotification } = useNotification();

  const safeT = (key: string, fallback: string) => {
    const v = t(key);
    return v === key ? fallback : v;
  };

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!name || !email || !password) {
      addNotification(safeT('register.error', 'Completa todos los campos.'), 'error');
      return;
    }

    setLoading(true);

    try {
      const res = await AuthService.register({ name, email, password });

      if ((res as any)?.mfaSetupRequired === true) {
        const setupToken = (res as any).setupToken as string;
        const otpauthUrl = (res as any).otpauthUrl as string;
        const qrSvg = ((res as any).qrSvg ?? null) as string | null;

        localStorage.setItem(MFA_PENDING_KEY, JSON.stringify({ type: 'register', setupToken, email, otpauthUrl, qrSvg }));
        addNotification('Te enviamos un código de 6 dígitos a tu correo.', 'info');
        navigate('/mfa/setup', { replace: true });
        return;
      }

      addNotification(safeT('register.success', '¡Registro exitoso! Tu cuenta está pendiente de aprobación por parte del administrador. Te notificaremos por email cuando sea aprobada.'), 'success');
      navigate('/login');
    } catch (error: any) {
      // Extraer mensaje específico del error
      let errorMessage = safeT('register.error', 'No se pudo completar el registro.');
      
      if (error?.payload?.message) {
        errorMessage = error.payload.message;
      } else if (error?.message) {
        errorMessage = error.message;
      }
      
      addNotification(errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#f4f6f8', fontFamily: "'Inter', sans-serif" }}>
      <div style={{ background: 'white', padding: '50px 40px', borderRadius: '20px', boxShadow: '0 10px 40px rgba(0,0,0,0.08)', width: '100%', maxWidth: '420px' }}>
        <div style={{ textAlign: 'center', marginBottom: 30 }}>
          <img src="/Profunding_Azul.png" alt="ProFunding" style={{ maxWidth: '220px', marginBottom: 20 }} />

          <h1 style={{ fontSize: '1.8rem', fontWeight: 800, color: '#333' }}>
            {safeT('register.title', 'Crear cuenta')}
          </h1>

          <p style={{ color: '#666', marginTop: 6 }}>
            {safeT('register.subtitle', 'Regístrate para acceder al portal de inversiones.')}
          </p>
        </div>

        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: 18 }}>
            <label style={{ fontWeight: 600 }}>{safeT('register.name_label', 'Nombre')}</label>
            <div style={{ position: 'relative', marginTop: 8 }}>
              <FiUser style={{ position: 'absolute', top: 14, left: 14, color: '#999' }} />
              <input
                type="text"
                value={name}
                onChange={e => setName(e.target.value)}
                placeholder={safeT('register.name_placeholder', 'Tu nombre completo')}
                required
                style={{ width: '100%', padding: '14px 14px 14px 42px', borderRadius: '10px', border: '1px solid #e0e0e0' }}
              />
            </div>
          </div>

          <div style={{ marginBottom: 18 }}>
            <label style={{ fontWeight: 600 }}>{safeT('register.email_label', 'Correo')}</label>
            <div style={{ position: 'relative', marginTop: 8 }}>
              <FiMail style={{ position: 'absolute', top: 14, left: 14, color: '#999' }} />
              <input
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder={safeT('register.email_placeholder', 'nombre@empresa.com')}
                required
                style={{ width: '100%', padding: '14px 14px 14px 42px', borderRadius: '10px', border: '1px solid #e0e0e0' }}
              />
            </div>
          </div>

          <div style={{ marginBottom: 26 }}>
            <label style={{ fontWeight: 600 }}>{safeT('register.password_label', 'Contraseña')}</label>
            <div style={{ position: 'relative', marginTop: 8 }}>
              <FiLock style={{ position: 'absolute', top: 14, left: 14, color: '#999' }} />
              <input
                type="password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                placeholder={safeT('register.password_placeholder', 'Mínimo 5 caracteres')}
                required
                style={{ width: '100%', padding: '14px 14px 14px 42px', borderRadius: '10px', border: '1px solid #e0e0e0' }}
              />
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            style={{
              width: '100%',
              padding: '15px',
              background: 'linear-gradient(135deg, #673ab7 0%, #512da8 100%)',
              color: 'white',
              border: 'none',
              borderRadius: '12px',
              fontSize: '1rem',
              fontWeight: 700,
              cursor: loading ? 'not-allowed' : 'pointer',
              opacity: loading ? 0.85 : 1
            }}
          >
            {loading ? safeT('register.loading', 'Creando...') : safeT('register.submit', 'Crear cuenta')}
          </button>
        </form>

        <div style={{ marginTop: 20, textAlign: 'center', fontSize: '0.95rem' }}>
          <span style={{ color: '#666' }}>{safeT('register.have_account', '¿Ya tienes cuenta?')}</span>{' '}
          <Link to="/login" style={{ color: '#673ab7', fontWeight: 800, textDecoration: 'none' }}>
            {safeT('register.go_login', 'Inicia sesión')}
          </Link>
        </div>

        <div style={{ marginTop: 24, textAlign: 'center' }}>
          <Link to="/" style={{ color: '#666', fontSize: '0.9rem', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <FiArrowLeft /> {safeT('public.back_to_home', 'Volver al inicio')}
          </Link>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;
